import { useMemo } from "react";
import { oids } from "@/configs/Settings";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useViewContext } from "@/contexts/ViewContext";
import { useFormatter } from "@/utils/useFormatter";
import type { TeleconsultoriaActor } from "../../types";
import type { RequesterMode } from "../types";

function practitionerName(practitioner: fhir5.Practitioner | undefined): string {
  const name = practitioner?.name?.[0];
  if (name?.text) return name.text;
  return [name?.given?.join(" "), name?.family].filter(Boolean).join(" ");
}

/**
 * Quem solicita a teleconsultoria (host do atendimento, na afiliada).
 * - Visão profissional: sempre o próprio usuário.
 * - Visão da clínica: qualquer profissional da organização (o usuário primeiro, se for um deles).
 * `requesterMode` (aba da página) manda; sem ele, vale o viewType global (como antes).
 */
export function useRequesterOptions(requesterMode?: RequesterMode) {
  const { user } = useSessionContext();
  const { viewType } = useViewContext();
  const { practitioner } = usePractitionerRoleContext();
  const isClinicView = requesterMode ? requesterMode === "choose" : viewType === "admin";

  const self = useMemo<TeleconsultoriaActor>(
    () => ({
      reference: `Practitioner/${oids.cpf}-${user?.username}`,
      display: useFormatter.formatNameDisplay(user?.name || ""),
    }),
    [user?.username, user?.name],
  );

  const options = useMemo<TeleconsultoriaActor[]>(() => {
    if (!isClinicView) return [self];
    const organizationPractitioners = (practitioner || [])
      .map((entry) => entry.resource)
      .filter((resource): resource is fhir5.Practitioner => Boolean(resource?.id))
      .map((resource) => ({
        reference: `Practitioner/${resource.id}`,
        display: practitionerName(resource) || "Profissional",
      }));
    const others = organizationPractitioners.filter((option) => option.reference !== self.reference);
    const isSelfInOrganization = organizationPractitioners.length !== others.length;
    return isSelfInOrganization ? [self, ...others] : others;
  }, [isClinicView, practitioner, self]);

  return { options, isClinicView, self };
}
